import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  label?: string,
  validationFn?: (input: string) => boolean,
  onChange: (input: React.ChangeEvent<HTMLTextAreaElement>) => void,
  errorMessage?: string,
  isDisabled?: boolean,
  placeholder?: string,
  defaultValue?: string,
  minLength?: number,
  maxLength?: number,
  helperText?: string
};

function TextArea({
  label = '', 
  validationFn = (input: string) => true, 
  errorMessage = '', 
  isDisabled = false, 
  placeholder = '', 
  onChange = () => null, 
  defaultValue = '', 
  minLength, 
  maxLength,
  helperText = ''
}: Props) {
  
  const { t } = useTranslation();
  const [isInvalidInput, setIsInvalidInput] = useState<boolean>();
  const [localValue, setLocalValue] = useState<string>('');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    setLocalValue(defaultValue);
    setCharCount(defaultValue?.length || 0);
  }, [defaultValue]);

  const displayError: string = useMemo(() => {
    if (isInvalidInput) {
      if (minLength && charCount < minLength) {
        return t('Minimum length is {{minLength}} characters', { minLength });
      }
      if (maxLength && charCount > maxLength) {
        return t('Maximum length is {{maxLength}} characters', { maxLength });
      }
      return t(errorMessage);
    }
    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvalidInput, charCount, minLength, maxLength, errorMessage]);

  const onChangeWithValidation = (input: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = input.currentTarget.value;
    const trimmedValue = value?.trim() ?? '';
    setCharCount(value.length);
    if (validationFn(trimmedValue) &&
      (!minLength || trimmedValue.length >= minLength) &&
      (!maxLength || trimmedValue.length <= maxLength)) {
      setIsInvalidInput(false);
      setLocalValue(value);
      onChange(input);
    } else {
      setIsInvalidInput(true);
      setLocalValue(value);
    }
  };

  return (
    <div className="flex-1 max-w-full">
      {label && <p className="text-sm font-extrabold">{ t(label) }:</p>}
      <textarea
        rows={5} 
        aria-invalid={isInvalidInput}
        maxLength={maxLength}
        className={`p-1 w-full border-stone-200 border rounded focus:outline-none ${isInvalidInput ? 'border-amber-700' : null}`}
        placeholder={t(placeholder) || ''} 
        onChange={(ev) => onChangeWithValidation(ev)}
        disabled={isDisabled}
        value={localValue}
      />
      <div className="flex flex-col">
        {helperText && <p className="text-xs text-gray-500 mt-1">{t(helperText)}</p>}
        <div className="flex justify-between text-xs">
          <p className="text-red-600 my-0">{displayError}</p>
          {maxLength && (
            <span className="text-gray-500">
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default TextArea;